import React, { FC, ReactElement, ReactNode, memo } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { clsx } from 'clsx';
import SVG from 'react-inlinesvg';
import { IconAxel, IconCommon, IconLogo, IconService } from '../../resources/images';
import { iconToCdn } from '../../utils/images';
import { useTheme } from '../../context/ThemeContext';

export interface SvgIconProps {
    icon: IconLogo | IconCommon | IconAxel | IconService;
    size?: 'inherit' | 'xsmall' | 'small' | 'mediumSmall' | 'medium' | 'large' | 'xxlarge' | 'xxxlarge' | 'none';
    color?:
        | 'default'
        | 'white'
        | 'black'
        | 'blue'
        | 'lightBlue'
        | 'grey'
        | 'darkGrey'
        | 'red'
        | 'lightBlack'
        | 'allBlack'
        | 'allWhite'
        | 'gold'
        | 'orange';
    fill?: string;
    stroke?: string;
    width?: number | string;
    height?: number | string;
    alert?: boolean;
    overwritePathFill?: boolean;
    overwritePathStroke?: boolean;
    className?: string;
    fallback?: ReactNode;
    fix?: number | string;
}

const SvgIcon: FC<SvgIconProps> = ({
    icon,
    size = 'medium',
    color = 'default',
    fill,
    stroke,
    width,
    height,
    alert,
    overwritePathFill,
    overwritePathStroke,
    className,
    fallback,
    fix,
}): ReactElement => {
    const classes = useStyles();
    const { mode } = useTheme();
    const isDarkMode = mode === 'dark';

    const src = iconToCdn(icon, isDarkMode);

    let overwriteStyle = '';
    const customStyle: any = {};

    if (fill) {
        customStyle.fill = fill;
    }
    if (stroke) {
        customStyle.stroke = stroke;
    }
    if (width) {
        customStyle.width = width;
    }
    if (height) {
        customStyle.height = height;
    }
    if (fix) {
        customStyle.minWidth = fix;
        customStyle.minHeight = fix;
    }

    if (overwritePathFill) {
        overwriteStyle = classes.overwritePathFill;
    }
    if (isDarkMode || overwritePathStroke) {
        overwriteStyle = clsx(overwriteStyle, classes.overwritePathStroke);
    }

    if (alert) {
        return (
            <>
                <SVG
                    src={src}
                    className={clsx(classes[size], classes[color], className, overwriteStyle)}
                    style={customStyle}
                />
                <SVG src={iconToCdn(IconCommon.Information)} className={clsx(classes.red, classes.iconError)} />
            </>
        );
    }

    return (
        <SVG
            src={src}
            className={clsx(classes[size], classes[color], className, overwriteStyle)}
            style={customStyle}
            loader={<div className={classes[size]} style={customStyle} />}
        >
            {fallback}
        </SVG>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        xsmall: {
            width: 14,
            height: 14,
        },
        small: {
            width: 16,
            height: 16,
        },
        mediumSmall: {
            width: 18,
            height: 18,
        },
        medium: {
            width: 20,
            height: 20,
        },
        large: {
            width: 24,
            height: 24,
        },
        xxlarge: {
            width: 34,
            height: 34,
        },
        xxxlarge: {
            width: 40,
            height: 40,
        },
        inherit: {
            width: 'inherit',
            fill: 'inherit',
            stroke: 'inherit',
        },
        none: {
            width: 0,
            height: 0,
            display: 'none',
        },
        default: {
            fill: 'none',
            stroke: theme.palette.colors.icon.lightGrey,
        },
        black: {
            fill: theme.palette.colors.icon.primary,
            stroke: theme.palette.colors.icon.primary,
        },
        lightBlack: {
            fill: theme.palette.primary.light,
            stroke: theme.palette.primary.light,
        },
        blue: {
            fill: theme.palette.colors.background.primaryBtn,
            stroke: theme.palette.colors.background.primaryBtn,
        },
        lightBlue: {
            fill: theme.palette.colors.background.focusElement,
            stroke: theme.palette.colors.background.focusElement,
        },
        grey: {
            fill: theme.palette.colors.icon.lightGrey,
            stroke: theme.palette.colors.icon.lightGrey,
        },
        darkGrey: {
            fill: theme.palette.colors.text.label,
            stroke: theme.palette.colors.text.label,
        },
        red: {
            fill: theme.palette.colors.text.error,
            stroke: theme.palette.colors.text.error,
        },
        white: {
            fill: theme.palette.primary.contrastText,
            stroke: theme.palette.primary.contrastText,
        },
        gold: {
            fill: theme.palette.colors.text.gold,
            stroke: theme.palette.colors.text.gold,
        },
        orange: {
            fill: theme.palette.colors.text.warning,
            stroke: theme.palette.colors.text.warning,
        },
        allBlack: {
            fill: theme.palette.colors.fixed.darkBlack,
            stroke: theme.palette.colors.fixed.darkBlack,
        },
        // Counterpart of `allBlack`, for icons sitting on a ground that stays dark in both modes.
        // `white` cannot serve there: it follows `primary.contrastText`, which is dark in dark mode.
        allWhite: {
            fill: theme.palette.colors.fixed.darkWhite,
            stroke: theme.palette.colors.fixed.darkWhite,
        },
        overwritePathFill: {
            '& path': {
                fill: 'inherit',
            },
        },
        overwritePathStroke: {
            '& path': {
                stroke: 'inherit',
            },
        },
        iconError: {
            position: 'relative',
            stroke: 'none',
            left: -5,
            top: -6,
            height: 10,
            width: 10,
        },
    }),
);

export default memo(SvgIcon);
